// ─────────────────────────────────────────────────────────────────────────────
// Client-side data layer for the Conjugarden social features, built on
// TanStack Query. All calls hit /api/data/* and require an Auth0 session
// (or a dev sign-in cookie locally). Components read these hooks; when the user
// isn't signed in, `useMe().authenticated` is false and the UI shows a gate.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react'
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  API_BASE,
  type MeResponse, type LeaderboardResponse, type FriendsResponse, type SearchResponse,
  type ProfileResponse, type GardenSummary, type WardenProfile, type LeaderboardEntry,
} from '../shared/conjugarden-api.js'

// ── Singleton query client ───────────────────────────────────────────────────
let _qc: QueryClient | null = null
function client(): QueryClient {
  if (!_qc) _qc = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } } })
  return _qc
}
export function ConjugardenProvider({ children }: { children: React.ReactNode }) {
  return <QueryClientProvider client={client()}>{children}</QueryClientProvider>
}

// ── Fetch helper ─────────────────────────────────────────────────────────────
async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(API_BASE + path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    ...init,
  })
  if (!res.ok && res.status !== 401) throw new Error(`api ${path} ${res.status}`)
  return res.json() as Promise<T>
}

// ── Auth URLs ────────────────────────────────────────────────────────────────
export const authUrls = {
  login: '/api/auth/signin/auth0',
  logout: '/api/auth/signout',
  devLogin: (name: string) => `/api/dev-auth/signin?name=${encodeURIComponent(name)}`,
  devLogout: '/api/dev-auth/signout',
}

// ── Queries ──────────────────────────────────────────────────────────────────
export function useMe() {
  const q = useQuery({ queryKey: ['me'], queryFn: () => api<MeResponse>('/me') })
  return { ...q, authenticated: !!q.data?.authenticated, profile: q.data?.profile, userId: q.data?.userId }
}

export function useLeaderboard(scope: 'global' | 'friends', lang: string, enabled = true) {
  return useQuery({
    queryKey: ['leaderboard', scope, lang], enabled,
    queryFn: () => api<LeaderboardResponse>(`/leaderboard?scope=${scope}&lang=${lang}`),
  })
}

export function useFriends(lang: string, enabled = true) {
  return useQuery({ queryKey: ['friends', lang], enabled, queryFn: () => api<FriendsResponse>(`/friends?lang=${lang}`) })
}

export function useSearchWardens(q: string, lang: string, enabled = true) {
  return useQuery({ queryKey: ['search', q, lang], enabled, queryFn: () => api<SearchResponse>(`/search?q=${encodeURIComponent(q)}&lang=${lang}`) })
}

export function useWardenProfile(userId: string | null, lang: string) {
  return useQuery({
    queryKey: ['profile', userId, lang], enabled: !!userId,
    queryFn: () => api<ProfileResponse>(`/profile/${encodeURIComponent(userId!)}?lang=${lang}`),
  })
}

// ── Mutations ────────────────────────────────────────────────────────────────
export function useUpdateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { handle?: string; avatar?: string; bio?: string }) =>
      api<{ profile: WardenProfile }>('/profile', { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['me'] }); qc.invalidateQueries({ queryKey: ['leaderboard'] }) },
  })
}

export function useToggleFriend(lang: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ friendId, add }: { friendId: string; add: boolean }) =>
      api<{ profile: WardenProfile }>('/friends', { method: add ? 'POST' : 'DELETE', body: JSON.stringify({ friendId }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] })
      qc.invalidateQueries({ queryKey: ['friends'] })
      qc.invalidateQueries({ queryKey: ['leaderboard', 'friends', lang] })
    },
  })
}

export function useSyncGarden() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (summary: GardenSummary) => api<{ ok: boolean }>('/sync', { method: 'POST', body: JSON.stringify({ summary }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['leaderboard'] }) },
  })
}

export type { LeaderboardEntry, WardenProfile, GardenSummary }
